import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ensureGooglePlacesLoaded } from '../../services/googlePlaces';
import { boundsOf, decodePolyline } from '../../utils/polyline';
import { dropoffIcon, pickupIcon, vehicleIcon, type MarkerColours } from './mapMarkers';
import { LIGHT_MAP_STYLE } from './mapStyles';

/**
 * Route and driver position for one trip.
 *
 * Decorative reinforcement, not the source of truth: every fact it shows —
 * status, ETA, addresses — is also written in text beside it, so a customer on
 * a blocked network or a screen reader loses nothing when the map does not
 * load. That is why a failure renders a quiet line rather than an error.
 */

interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface TripMapProps {
  routePolyline?: string | null;
  pickup?: LatLngLiteral | null;
  dropoff?: LatLngLiteral | null;
  driver?: LatLngLiteral | null;
  /** Compass heading in degrees, when the driver feed provides one. */
  driverHeading?: number | null;
  label: string;
}

/**
 * Design tokens, read at runtime rather than hardcoded.
 *
 * The map is drawn in JavaScript, so it cannot inherit CSS — but it must not
 * become the one surface that quietly drifts off-palette. Reading the custom
 * properties keeps it following the same theme as everything else; the
 * fallbacks match `theme.css` and only apply if a token is ever renamed.
 */
function token(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/** Fraction of the viewport kept clear at each edge when following the driver. */
const EDGE_PADDING = 0.18;

/**
 * Duration of the wagon's glide between position ticks.
 *
 * The brief's number, and it is doing real work: with no tween the marker jumps
 * the whole distance the instant a tick lands, which reads as a teleport rather
 * than a vehicle. Longer than this and the wagon lags visibly behind its own
 * ETA. Linear, not eased — an ease-out makes a constant-speed truck look like it
 * brakes into every poll.
 */
const GLIDE_MS = 150;

/** The four brand colours the marker art is tinted with. */
function markerColours(): MarkerColours {
  return {
    route: token('--sw-route', '#0f6b62'),
    asphalt: token('--sw-asphalt', '#14181b'),
    paper: token('--sw-paper', '#faf9f6'),
    signal: token('--sw-signal', '#ffc93c'),
  };
}

const TripMap: React.FC<TripMapProps> = ({
  routePolyline,
  pickup,
  dropoff,
  driver,
  driverHeading,
  label,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const driverMarkerRef = useRef<any>(null);
  const overlaysRef = useRef<any[]>([]);
  /* Where the wagon is actually drawn, which trails the last reported fix while
     a glide is in flight. Kept in a ref, not state: it changes every animation
     frame and nothing in the tree renders from it. */
  const driverDrawnRef = useRef<LatLngLiteral | null>(null);
  const glideFrameRef = useRef(0);
  /* Google fires `dragstart` for programmatic pans as well as user gestures, so
     without this the map's own follow-pan switches following off — the control
     appears unprompted and tracking silently stops. */
  const selfPanningRef = useRef(false);
  const [failed, setFailed] = useState(false);
  /* Auto-follow yields to the customer the moment they move the map, and comes
     back only when they ask for it. A map that keeps yanking itself back is
     unusable for someone trying to look at the destination. */
  const [following, setFollowing] = useState(true);

  const recentre = useCallback(() => {
    setFollowing(true);
    if (mapRef.current && driver) {
      selfPanningRef.current = true;
      mapRef.current.panTo(driver);
      window.setTimeout(() => {
        selfPanningRef.current = false;
      }, 400);
    }
  }, [driver]);

  useEffect(() => {
    let cancelled = false;

    ensureGooglePlacesLoaded()
      .then(() => {
        if (cancelled || !containerRef.current) return;

        const google = (window as any).google;
        if (!google?.maps?.Map) {
          setFailed(true);
          return;
        }

        const routeColour = token('--sw-route', '#0f6b62');
        const paper = token('--sw-paper', '#faf9f6');

        const map = new google.maps.Map(containerRef.current, {
          disableDefaultUI: true,
          zoomControl: true,
          /* Zoom moved off the bottom-right, which is the only corner the
             "Follow driver" button can take: bottom-left is Google's logo and
             the bottom edge carries the attribution, neither of which may be
             covered. Stacking the button above the zoom control instead would
             mean hardcoding that control's height — a magic number that breaks
             the day Google restyles it. Moving the control is the version with
             nothing to keep in sync. */
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
          /* Keep the basemap quiet so the route reads as the subject. The two
             rules that used to live here — POI and transit off — are the first
             thing the brand style does too, and it goes on to pull land, water
             and roads onto the palette. Without that the map was the one
             surface on the page still wearing Google's colours. */
          styles: LIGHT_MAP_STYLE,
          center: pickup ?? dropoff ?? { lat: -32.985, lng: 27.871 },
          zoom: 12,
        });
        mapRef.current = map;
        driverMarkerRef.current?.setMap?.(null);
        driverMarkerRef.current = null;
        /* Drop the glide with the marker it was moving, or the next tick would
           interpolate from a position on a map that no longer exists. */
        window.cancelAnimationFrame(glideFrameRef.current);
        driverDrawnRef.current = null;

        map.addListener('dragstart', () => {
          if (selfPanningRef.current) return;
          setFollowing(false);
        });

        overlaysRef.current.forEach((overlay) => overlay.setMap?.(null));
        overlaysRef.current = [];

        const points = decodePolyline(routePolyline);

        if (points.length >= 2) {
          /* Two strokes: a paper-coloured casing under the route line. Standard
             cartography — it keeps a mid-tone line legible over both the pale
             road fill and the darker green of parks, which a single stroke does
             not. */
          overlaysRef.current.push(
            new google.maps.Polyline({
              map,
              path: points,
              strokeColor: paper,
              strokeOpacity: 0.9,
              strokeWeight: 9,
              zIndex: 1,
            }),
            new google.maps.Polyline({
              map,
              path: points,
              strokeColor: routeColour,
              strokeOpacity: 1,
              strokeWeight: 5,
              zIndex: 2,
            }),
          );
        }

        /* The rebrand pins, anchored at the tip. Two shapes that differ in fill
           and in the glyph they carry, so the ends of the route stay tellable
           apart without colour alone — the old pair was one circle filled and
           the same circle hollow, which is a colour distinction wearing a
           shape's clothes. */
        const colours = markerColours();

        const endpoint = (position: LatLngLiteral, title: string, icon: object) =>
          new google.maps.Marker({
            map,
            position,
            title,
            zIndex: 3,
            icon,
          });

        if (pickup) {
          overlaysRef.current.push(endpoint(pickup, 'Pickup', pickupIcon(google, colours)));
        }
        if (dropoff) {
          overlaysRef.current.push(endpoint(dropoff, 'Drop-off', dropoffIcon(google, colours)));
        }

        const fitTo = points.length >= 2 ? points : [pickup, dropoff].filter(Boolean);
        const bounds = boundsOf(fitTo as LatLngLiteral[]);

        /* Fit once the map reports it has laid out. Fitting immediately uses the
           container's pre-layout height, which letterboxes the route and lets it
           run off the bottom of the frame. */
        const fit = () => {
          if (!bounds) return;
          selfPanningRef.current = true;
          map.fitBounds(
            new google.maps.LatLngBounds(
              { lat: bounds.south, lng: bounds.west },
              { lat: bounds.north, lng: bounds.east },
            ),
            /* Generous padding so an endpoint marker is never half-clipped by
               the frame edge — a pin touching the border reads as "off screen". */
            { top: 56, right: 40, bottom: 56, left: 40 },
          );
          window.setTimeout(() => {
            selfPanningRef.current = false;
          }, 400);
        };

        google.maps.event.addListenerOnce(map, 'idle', fit);
        /* And again whenever the frame changes size — a narrower viewport or a
           rotated phone would otherwise keep the wide fit and run the route off
           the edge.

           Debounced, and deliberately so: ResizeObserver fires while the
           element is still settling, and Google has not yet recalculated its
           own dimensions at that instant. Fitting immediately measures the old
           size and leaves the route overflowing — which is the bug this fixes,
           not a precaution. */
        if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
          let refitTimer = 0;
          const observer = new ResizeObserver(() => {
            window.clearTimeout(refitTimer);
            refitTimer = window.setTimeout(fit, 180);
          });
          observer.observe(containerRef.current);
          overlaysRef.current.push({
            setMap: () => {
              window.clearTimeout(refitTimer);
              observer.disconnect();
            },
          });
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [
    routePolyline,
    pickup?.lat,
    pickup?.lng,
    dropoff?.lat,
    dropoff?.lng,
  ]);

  /* The driver moves; rebuilding the map for each frame would reset the camera
     and fight the customer for control of it. Only the marker is updated. */
  useEffect(() => {
    const google = (window as any).google;
    const map = mapRef.current;
    if (!google?.maps || !map || !driver) return;

    const hasHeading = typeof driverHeading === 'number' && Number.isFinite(driverHeading);
    /* The whole puck turns, so the chevrons read as the direction of travel.
       With no heading in the feed it stays at 0° — a guessed bearing points the
       customer down a road the driver may not be on. */
    const bearing = hasHeading ? (driverHeading as number) : 0;

    /**
     * A billboarded marker — `flat: false` is the default and is stated here
     * because it is the property that matters: a flat marker rotates with the
     * map, so the moment the basemap is rotated the puck would turn with it and
     * the chevrons would stop meaning north-relative bearing. Rotation lives in
     * the art instead, so the wagon points the right way whatever the camera
     * does.
     */
    const icon = vehicleIcon(google, markerColours(), bearing);

    if (!driverMarkerRef.current) {
      driverMarkerRef.current = new google.maps.Marker({
        map,
        position: driver,
        title: 'Driver',
        flat: false,
        optimized: false,
        zIndex: 4,
        icon,
      });
      driverDrawnRef.current = driver;
    } else {
      driverMarkerRef.current.setIcon(icon);

      /* Glide to the new fix rather than snapping to it. Interpolating in
         lat/lng is fine at the distance one poll covers — the projection error
         over a few hundred metres is far below a pixel at these zooms, and the
         alternative is a great-circle solve per frame for no visible gain. */
      const from = driverDrawnRef.current ?? driver;
      const start = performance.now();
      window.cancelAnimationFrame(glideFrameRef.current);

      const step = (now: number) => {
        const progress = Math.min((now - start) / GLIDE_MS, 1);
        const position = {
          lat: from.lat + (driver.lat - from.lat) * progress,
          lng: from.lng + (driver.lng - from.lng) * progress,
        };
        driverDrawnRef.current = position;
        driverMarkerRef.current?.setPosition(position);
        if (progress < 1) glideFrameRef.current = window.requestAnimationFrame(step);
      };

      glideFrameRef.current = window.requestAnimationFrame(step);
    }

    if (!following) return;

    /**
     * Keep the driver inside a padded inset of the viewport rather than only
     * inside its bounds. A marker exactly on the boundary is drawn half outside
     * the frame and reads as lost, so the map pans once the driver crosses into
     * the outer margin — not on every frame, which would make the map crawl.
     */
    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const latMargin = (ne.lat() - sw.lat()) * EDGE_PADDING;
    const lngMargin = (ne.lng() - sw.lng()) * EDGE_PADDING;

    const outsideSafeArea =
      driver.lat > ne.lat() - latMargin ||
      driver.lat < sw.lat() + latMargin ||
      driver.lng > ne.lng() - lngMargin ||
      driver.lng < sw.lng() + lngMargin;

    if (outsideSafeArea) {
      selfPanningRef.current = true;
      map.panTo(driver);
      window.setTimeout(() => {
        selfPanningRef.current = false;
      }, 400);
    }
  }, [driver, driverHeading, following]);

  /* A glide is the one thing here that outlives its own effect — it is still
     scheduled when the customer navigates away, and would call setPosition on a
     torn-down marker. Cancelled on unmount only, so it is not interrupted by
     the position updates that legitimately restart it. */
  useEffect(() => () => window.cancelAnimationFrame(glideFrameRef.current), []);

  if (failed) {
    return <p className="sw-estimate-note">Map unavailable. Trip details are listed below.</p>;
  }

  return (
    <div className="sw-trip-map">
      <div aria-label={label} className="sw-map-frame" ref={containerRef} role="img" />
      {driver && !following ? (
        <button className="sw-btn-secondary sw-map-recentre" onClick={recentre} type="button">
          Follow driver
        </button>
      ) : null}
    </div>
  );
};

export default TripMap;
